/** Core domain model. Deliberately independent of Google Drive so that the mock
  * source used in demo mode and the real Drive source are interchangeable. */

export interface Track {
   /** Drive file id, or a synthetic id in demo mode. */
   id: string;
   /** Display name with the extension and any leading track number stripped. */
   name: string;
   fileName: string;
   mimeType: string;
   sizeBytes: number;
   /** Filled in once the audio element reports it; cached thereafter. */
   durationSec?: number;
}

export interface Chapter {
   title: string;
   /** Index of the track this chapter lives in. */
   trackIndex: number;
   /** Offset from the start of that track, in seconds. */
   startSec: number;
}

export type BookKind = 'folder' | 'single';

export interface Book {
   /** Drive folder id for multi-file books, file id for single-file books. */
   id: string;
   title: string;
   author?: string;
   /** Resolved cover image URL, if one was found. */
   coverUrl?: string;
   kind: BookKind;
   tracks: Track[];
   totalSizeBytes: number;
}

export interface BookProgress {
   trackIndex: number;
   positionSec: number;
   /** Total duration of the whole book, once known. 0 while still unknown. */
   durationSec: number;
   /** ISO 8601. The merge key for cross-device sync. */
   updatedAt: string;
   /** Human-readable label of the device that last wrote this entry. */
   device: string;
   finished: boolean;
}

export interface ProgressFile {
   version: 1;
   books: Record<string, BookProgress>;
}

export const EMPTY_PROGRESS: ProgressFile = { version: 1, books: {} };

/**
  * Everything the app needs from a library backend. `mock.ts` and the real
  * Drive source both implement this, which is what lets the entire UI, player
  * and sync loop be exercised without a Google account.
  */
export interface LibrarySource {
   readonly kind: 'demo' | 'drive';
    /** Label shown in the header, e.g. "Demo library" or the Drive folder name. */
    readonly label: string;

    listBooks(): Promise<Book[]>;

    /** A URL the <audio> element can load directly. May be async: the Drive
     * source falls back to buffering a Blob when no service worker is
     * available to proxy range requests. */
    trackUrl(track: Track): string | Promise<string>;

    /** Chapters for a book, if the source can determine them. */
    chaptersFor(book: Book): Promise<Chapter[]>;

    loadProgress(): Promise<ProgressFile | null>;
    saveProgress(progress: ProgressFile): Promise<void>;
}
